import { useDeviceStore, useLocaleStore } from '@/stores'
import { useConfigName } from '@/composables/useConfigName'
import { type Config, type mouseUserCustom, NoticeEvents, type BaseDevice } from '@rapoo/device-sdk'
import { storeToRefs } from 'pinia'
import { watch } from 'vue'

/**
 * 构建一个事件通知的 register / unRegister 对
 * 每个 device 注册时创建独立的 handler，闭包捕获 device model
 */
function createNoticeHandler<T>(
  eventName: string,
  format: (data: T, model: string, device: BaseDevice) => { title: string; message: string },
  type: string,
) {
  const handlerMap = new Map<string, (data: T) => void>()

  return {
    register(device: BaseDevice) {
      const deviceId = device.id
      const model = device.manifest.model ?? ''
      const handler = (data: T) => {
        console.log(`[Notice] ${eventName}`, data)
        window.electronAPI?.sendNotice({
          title: format(data, model, device).title,
          message: format(data, model, device).message,
          type,
        })
      }
      handlerMap.set(deviceId, handler)
      device.on(eventName, handler)
    },
    unRegister(device: BaseDevice) {
      const deviceId = device.id
      const handler = handlerMap.get(deviceId)
      if (handler) {
        device.off(eventName, handler)
        handlerMap.delete(deviceId)
      }
    },
  }
}

export function useNoticeReport() {
  const deviceStore = useDeviceStore()
  const { deviceList } = storeToRefs(deviceStore)

  const { t } = useLocaleStore()

  const notices = [
    createNoticeHandler<Config>(
      NoticeEvents.CfgChanged,
      (cfg, model, device) => ({
        title: `${model} ${t('notice.cfgChanged')}`,
        message: useConfigName(cfg, device),
      }),
      'cfg_switch',
    ),

    createNoticeHandler<Pick<mouseUserCustom, 'cpiLev' | 'xCpiVal' | 'yCpiVal'>>(
      NoticeEvents.DpiChanged,
      (d, model) => ({
        title: `${model} ${t('notice.dpiChanged')}`,
        message: `x:${d.xCpiVal} y:${d.yCpiVal}`,
      }),
      'dpi_switch',
    ),

    createNoticeHandler<number>(
      NoticeEvents.RTChanged,
      (rt, model) => ({
        title: `${model} ${t('notice.rtChanged')}`,
        message: `RT:${rt}`,
      }),
      'rt_switch',
    ),
  ]

  /**
   * 递归收集设备及其所有子设备
   */
  const collectDevices = (devices: BaseDevice[]): BaseDevice[] => {
    const result: BaseDevice[] = []
    for (const device of devices) {
      result.push(device)
      if (device.subDevices?.length) {
        result.push(...collectDevices(device.subDevices))
      }
    }
    return result
  }

  const registerAll = (devices: BaseDevice[]) => {
    const all = collectDevices(devices)
    all.forEach((device) => {
      notices.forEach((n) => n.register(device))
    })
  }

  const unRegisterAll = (devices: BaseDevice[]) => {
    const all = collectDevices(devices)
    all.forEach((device) => {
      notices.forEach((n) => n.unRegister(device))
    })
  }

  watch(
    () => deviceList.value,
    (devices, oldDevices) => {
      if (oldDevices) unRegisterAll(oldDevices)
      if (devices) registerAll(devices)
    },
    { immediate: true },
  )

  return {
    registerNotice: () => registerAll(deviceList.value),
    unRegisterNotice: () => unRegisterAll(deviceList.value),
  }
}
